/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import { css, jsx } from '@emotion/react';
import type { IntlShape } from 'react-intl';

import { messages as blockMenuMessages } from '@atlaskit/editor-common/messages/block-menu';
import type { Command } from '@atlaskit/editor-common/types/command';
import type {
	DropdownOptionT,
	FloatingToolbarOverflowDropdown,
} from '@atlaskit/editor-common/types/floating-toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { ContentNodeWithPos } from '@atlaskit/editor-prosemirror/utils';
import CopyIcon from '@atlaskit/icon/core/copy';
import DatabaseIcon from '@atlaskit/icon/core/database';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import LinkIcon from '@atlaskit/icon/core/link';
import { BUILTIN_TOOLBAR_KEYS } from '@atlaskit/native-embeds-common/constants';
import type {
	EditorToolbarAction,
	EditorToolbarHandler,
	EditorToolbarHandlers,
	NativeEmbedParameterValues,
} from '@atlaskit/native-embeds-common/types';
import Toggle from '@atlaskit/toggle/toggle';

import { nativeEmbedBlockMenuMessages } from '../../messages/block-menu';
import { nativeEmbedToolbarMessages } from '../../messages/native-embed-toolbar';
import type {
	EditorPluginNativeEmbedsPlugin,
	EditorToolbarHandlersWithDefaults,
} from '../../nativeEmbedsPluginType';
import { createToggleAlwaysShowTitle, getAlwaysShowTitleState } from '../commands';

const toggleButtonStyles = css({
	display: 'flex',
	alignItems: 'center',
	cursor: 'default',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
	'&&': {
		background: 'none',
		border: 'none',
		padding: 0,
		minHeight: 'unset',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Remove margin from Toggle to keep row consistent
	'& label': {
		margin: 0,
	},
});

type DeleteHoverProps = Pick<
	DropdownOptionT<Command>,
	'onMouseEnter' | 'onMouseLeave' | 'onFocus' | 'onBlur'
>;

const createHandlerCommand =
	(handler?: () => void): Command =>
	() => {
		handler?.();
		return true;
	};

type MenuIconSpacing = 'spacious' | undefined;

function convertCustomActionToDropdownOption(
	action: EditorToolbarAction,
	parameters: NativeEmbedParameterValues,
	editorToolbarHandlers: EditorToolbarHandlersWithDefaults,
	iconSpacing: MenuIconSpacing,
): DropdownOptionT<Command> {
	const IconComponent = action.icon;
	const handler = editorToolbarHandlers[action.handlerKey as keyof EditorToolbarHandlers];
	return {
		id: `native-embed-${action.key}`,
		title: action.label,
		icon: <IconComponent color="currentColor" spacing={iconSpacing} label="" />,
		disabled: action.disabled,
		onClick: createHandlerCommand(
			handler ? () => (handler as EditorToolbarHandler)(parameters) : undefined,
		),
	};
}

/**
 * Creates a registry of built-in "More Options" dropdown items.
 */
function createBuiltinMoreOptionsRegistry(
	api: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined,
	selectedNativeEmbed: ContentNodeWithPos,
	deleteHoverProps: DeleteHoverProps,
	editorToolbarHandlers: EditorToolbarHandlersWithDefaults,
	intl: IntlShape,
	iconSpacing: MenuIconSpacing,
): Record<string, DropdownOptionT<Command>> {
	const toggleAlwaysShowTitle = createToggleAlwaysShowTitle(api, selectedNativeEmbed.node);
	return {
		[BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE]: {
			id: 'native-embed-always-show-title',
			title: intl.formatMessage(nativeEmbedToolbarMessages.alwaysShowTitle),
			icon: <EyeOpenIcon color="currentColor" spacing={iconSpacing} label="" />,
			elemAfter: (
				<button
					type="button"
					css={toggleButtonStyles}
					onClick={(e) => {
						e.stopPropagation();
						toggleAlwaysShowTitle();
					}}
				>
					<Toggle
						isChecked={getAlwaysShowTitleState(selectedNativeEmbed.node)}
						label={intl.formatMessage(nativeEmbedToolbarMessages.alwaysShowTitle)}
					/>
				</button>
			),
			onClick: editorToolbarHandlers.onShowTitleClick(),
		},
		[BUILTIN_TOOLBAR_KEYS.SET_EMBED_TYPE]: {
			id: 'native-embed-set-embed-type',
			title: intl.formatMessage(nativeEmbedToolbarMessages.setEmbedType),
			icon: <DatabaseIcon color="currentColor" spacing={iconSpacing} label="" />,
			onClick: editorToolbarHandlers.onSetEmbedTypeClick(),
		},
		[BUILTIN_TOOLBAR_KEYS.COPY]: {
			id: 'native-embed-copy',
			title: intl.formatMessage(nativeEmbedBlockMenuMessages.copy),
			icon: <CopyIcon color="currentColor" spacing={iconSpacing} label="" />,
			onClick: editorToolbarHandlers.onCopyClick(),
		},
		/**
		 * @private
		 * @deprecated Use COPY instead. Will be removed in a follow-up PR.
		 */
		[BUILTIN_TOOLBAR_KEYS.COPY_LINK]: {
			id: 'native-embed-copy-link',
			title: intl.formatMessage(blockMenuMessages.copyLink),
			icon: <LinkIcon color="currentColor" spacing={iconSpacing} label="" />,
			onClick: editorToolbarHandlers.onCopyLinkClick(),
		},
		[BUILTIN_TOOLBAR_KEYS.DELETE]: {
			id: 'native-embed-delete',
			title: intl.formatMessage(blockMenuMessages.deleteBlock),
			icon: <DeleteIcon color="currentColor" spacing={iconSpacing} label="" />,
			onClick: editorToolbarHandlers.onDeleteClick(),
			...deleteHoverProps,
		},
	};
}

const DEFAULT_MORE_OPTIONS: string[] = [
	BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE,
	BUILTIN_TOOLBAR_KEYS.SET_EMBED_TYPE,
	BUILTIN_TOOLBAR_KEYS.SEPARATOR,
	BUILTIN_TOOLBAR_KEYS.COPY,
	BUILTIN_TOOLBAR_KEYS.DELETE,
];

function resolveMoreOptions(
	items: string[],
	builtinRegistry: Record<string, DropdownOptionT<Command>>,
	customActions: Record<string, EditorToolbarAction> | undefined,
	parameters: NativeEmbedParameterValues,
	editorToolbarHandlers: EditorToolbarHandlersWithDefaults,
	iconSpacing: MenuIconSpacing,
): (DropdownOptionT<Command> | { type: 'separator' })[] {
	const options: (DropdownOptionT<Command> | { type: 'separator' })[] = [];

	for (const key of items) {
		if (key === BUILTIN_TOOLBAR_KEYS.SEPARATOR) {
			options.push({ type: 'separator' });
		} else if (key in builtinRegistry) {
			options.push(builtinRegistry[key]);
		} else if (customActions && key in customActions) {
			options.push(
				convertCustomActionToDropdownOption(
					customActions[key],
					parameters,
					editorToolbarHandlers,
					iconSpacing,
				),
			);
		}
	}

	return options;
}

/**
 * Returns the "More options" overflow dropdown configuration for the native embed floating toolbar.
 *
 * When `moreItems` is provided from the manifest, it determines which items appear
 * and in what order. Custom actions from the top-level `customActions` can be referenced
 * by key alongside built-in dropdown keys. Handlers come from manifest `customActionHandlers`.
 * Falls back to the default items when omitted.
 */
export const getMoreOptionsDropdown = (
	api: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined,
	selectedNativeEmbed: ContentNodeWithPos,
	deleteHoverProps: DeleteHoverProps,
	editorToolbarHandlers: EditorToolbarHandlersWithDefaults,
	parameters: NativeEmbedParameterValues,
	intl: IntlShape,
	moreItems?: string[],
	customActions?: Record<string, EditorToolbarAction>,
	compact?: boolean,
): FloatingToolbarOverflowDropdown<Command> => {
	// Compact matches the media "More options" menu: default icon spacing and menu width.
	const iconSpacing: MenuIconSpacing = compact ? undefined : 'spacious';
	const builtinRegistry = createBuiltinMoreOptionsRegistry(
		api,
		selectedNativeEmbed,
		deleteHoverProps,
		editorToolbarHandlers,
		intl,
		iconSpacing,
	);
	const items = moreItems ?? DEFAULT_MORE_OPTIONS;

	const options = resolveMoreOptions(
		items,
		builtinRegistry,
		customActions,
		parameters,
		editorToolbarHandlers,
		iconSpacing,
	);

	return {
		id: 'native-embed-more-options-button',
		testId: 'native-embed-more-options-button',
		type: 'overflow-dropdown',
		dropdownWidth: compact ? undefined : 250,
		options,
	};
};
